import React, { useEffect, useMemo, useRef, useState } from "react";
import { Formik } from "formik";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../components/Loader";
import ErrorMessage from "../components/ErrorMessage";
import DeleteModal from "../components/modals/DeleteModal";
import ReactPagination from "../layout/ReactPagination";
import PaginationDropdown from "../layout/PaginationDropdown";
import { AddPartSchema } from "../auth/Schema";
import {
  addPart,
  acceptExtraPartRequest,
  deletePart,
  fetchExtraPartRequests,
  fetchPartById,
  fetchParts,
  updatePart,
} from "../redux/actions/servicePresetsActions";
import { BASE_URL } from "../routes/BackendRoutes";
import { curSym } from "../auth/Pip";

const WARRANTY_TYPE_OPTIONS = ["DAYS", "MONTHS", "YEARS"];
const PARTS_TAB = "PARTS";
const REQUESTED_PARTS_TAB = "REQUESTED_PARTS";

const defaultPartValues = {
  name: "",
  original_cost: "",
  boat_owner_cost: "",
  stock_quantity: "",
  low_stock_alert: "",
  // warranty_duration: "",
  // warranty_type: "MONTHS",
  part_number: "",
  manufacturer: "",
  serial_number: "",
  // installation_date: "",
  // warranty_start_date: "",
  // warranty_end_date: "",
  // warranty_document: null,
  // part_image: null,
};

const getFileUrl = (filePath) => {
  if (!filePath) {
    return "";
  }

  if (/^https?:\/\//i.test(filePath)) {
    return filePath;
  }

  const normalizedBase = (
    process.env.REACT_APP_API_BASE_URL || BASE_URL
  ).replace(/\/+$/, "");
  const normalizedPath = String(filePath).replace(/^\/+/, "");

  return `${normalizedBase}/${normalizedPath}`;
};

const getPartStatus = (part) => {
  if (Number(part?.stock_quantity) === 0) {
    return "Out of Stock";
  }

  if (part?.low_stock) {
    return "Low Stock";
  }

  return "In Stock";
};

const buildPartPayload = (values, overrides = {}) => {
  const finalValues = {
    ...defaultPartValues,
    ...values,
    ...overrides,
  };

  return {
    name: String(finalValues.name ?? "").trim(),
    original_cost:
      finalValues.original_cost === "" ? "" : Number(finalValues.original_cost),
    boat_owner_cost:
      finalValues.boat_owner_cost === ""
        ? ""
        : Number(finalValues.boat_owner_cost),
    stock_quantity:
      finalValues.stock_quantity === ""
        ? ""
        : Number(finalValues.stock_quantity),
    low_stock_alert:
      finalValues.low_stock_alert === ""
        ? ""
        : Number(finalValues.low_stock_alert),
    // warranty_duration:
    //   finalValues.warranty_duration === ""
    //     ? ""
    //     : Number(finalValues.warranty_duration),
    // warranty_type: String(finalValues.warranty_type ?? "").trim(),
    part_number: String(finalValues.part_number ?? "").trim(),
    manufacturer: String(finalValues.manufacturer ?? "").trim(),
    serial_number: String(finalValues.serial_number ?? "").trim(),
  };
};

const PartUsage = () => {
  const dispatch = useDispatch();
  const addPartModalRef = useRef(null);
  const editPartModalRef = useRef(null);
  const acceptRequestModalRef = useRef(null);
  const [isToggle, setIsToggle] = useState(false);
  const [activeTab, setActiveTab] = useState(PARTS_TAB);
  const [selectedPartId, setSelectedPartId] = useState(null);
  const [selectedPart, setSelectedPart] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(0);
  const [usersPerPage, setUserPerPages] = useState(5);
  const {
    partsData = [],
    partsLoading = false,
    selectedPartDetail = null,
    requestedPartsData = [],
    requestedPartsLoading = false,
  } = useSelector((state) => state?.servicePresetsReducer || {});

  const onHandleClick = () => {
    setIsToggle((prev) => !prev);
  };

  const closeModal = (modalRef) => {
    if (modalRef.current) {
      modalRef.current.classList.remove("show");
      modalRef.current.setAttribute("aria-hidden", "true");
      modalRef.current.style.display = "none";
    }

    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("padding-right");
    document
      .querySelectorAll(".modal-backdrop")
      .forEach((backdrop) => backdrop.remove());
  };

  const closeAddPartModal = () => {
    closeModal(addPartModalRef);
  };

  const closeEditPartModal = () => {
    closeModal(editPartModalRef);
    setSelectedPart(null);
  };

  const closeAcceptRequestModal = () => {
    closeModal(acceptRequestModalRef);
    setSelectedRequest(null);
  };

  const resolvedSelectedPart =
    selectedPartDetail?.id === selectedPart?.id
      ? selectedPartDetail
      : selectedPart;

  const editInitialValues = useMemo(
    () => ({
      name: resolvedSelectedPart?.name || "",
      original_cost: resolvedSelectedPart?.original_cost ?? "",
      boat_owner_cost: resolvedSelectedPart?.boat_owner_cost ?? "",
      stock_quantity: resolvedSelectedPart?.stock_quantity ?? "",
      low_stock_alert: resolvedSelectedPart?.low_stock_alert ?? "",
      // warranty_duration: resolvedSelectedPart?.warranty_duration ?? "",
      // warranty_type: resolvedSelectedPart?.warranty_type || "MONTHS",
      part_number: resolvedSelectedPart?.part_number || "",
      manufacturer: resolvedSelectedPart?.manufacturer || "",
      serial_number: resolvedSelectedPart?.serial_number || "",
    }),
    [resolvedSelectedPart],
  );

  const acceptRequestInitialValues = useMemo(
    () => ({
      ...defaultPartValues,
      name: selectedRequest?.partName || "",
      stock_quantity: selectedRequest?.unitsUsed ?? "",
    }),
    [selectedRequest],
  );

  const handleAddPart = (values, { resetForm, setSubmitting }) => {
    dispatch(
      addPart({
        payload: buildPartPayload(values, {
          name: values?.name?.trim(),
        }),
        callback: (response, error) => {
          setSubmitting(false);

          if (error || !response?.success) {
            return;
          }

          dispatch(fetchParts());
          resetForm();
          closeAddPartModal();
        },
      }),
    );
  };

  const handleDeletePart = () => {
    if (!selectedPartId) {
      return;
    }

    dispatch(
      deletePart({
        payload: selectedPartId,
        callback: (response) => {
          if (response?.success) {
            dispatch(fetchParts());
            setSelectedPartId(null);
          }
        },
      }),
    );
  };

  const handleEditPart = (values, { setSubmitting }) => {
    if (!selectedPart?.id) {
      setSubmitting(false);
      return;
    }

    dispatch(
      updatePart({
        payload: {
          id: selectedPart.id,
          data: buildPartPayload(values, {
            name:
              resolvedSelectedPart?.name?.trim() ||
              selectedPart?.name?.trim() ||
              values?.name?.trim(),
          }),
        },
        callback: (response, error) => {
          setSubmitting(false);

          if (error || !response?.success) {
            return;
          }

          dispatch(fetchParts());
          closeEditPartModal();
        },
      }),
    );
  };

  const handleAcceptRequest = (values, { setSubmitting, resetForm }) => {
    if (!selectedRequest?.id) {
      setSubmitting(false);
      return;
    }

    const partPayload = buildPartPayload(values, {
      name: selectedRequest?.partName || values?.name,
    });

    dispatch(
      acceptExtraPartRequest({
        payload: {
          requestId: selectedRequest.id,
          original_cost: partPayload.original_cost,
          boat_owner_cost: partPayload.boat_owner_cost,
          stock_quantity: partPayload.stock_quantity,
          // warranty_duration: partPayload.warranty_duration,
          // warranty_type: partPayload.warranty_type,
          part_number: partPayload.part_number,
          manufacturer: partPayload.manufacturer,
          serial_number: partPayload.serial_number,
          low_stock_alert: partPayload.low_stock_alert,
        },
        callback: (response, error) => {
          setSubmitting(false);

          if (error || !response?.success) {
            return;
          }

          resetForm();
          closeAcceptRequestModal();
          dispatch(fetchExtraPartRequests());
          dispatch(fetchParts());
        },
      }),
    );
  };

  const handleEditClick = (part) => {
    setSelectedPart(part);
    dispatch(
      fetchPartById({
        payload: part.id,
      }),
    );
  };

  useEffect(() => {
    dispatch(fetchParts());
    dispatch(fetchExtraPartRequests());
  }, [dispatch]);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchText, selectedStatus, activeTab]);

  const filteredParts = useMemo(() => {
    return (partsData || []).filter((item) => {
      const partStatus = getPartStatus(item);
      const normalizedSearch = searchText.trim().toLowerCase();
      const matchesSearch = normalizedSearch
        ? [item?.name, item?.part_number, item?.manufacturer]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(normalizedSearch))
        : true;
      const matchesStatus =
        selectedStatus === "All Status" ? true : partStatus === selectedStatus;

      return matchesSearch && matchesStatus;
    });
  }, [partsData, searchText, selectedStatus]);

  const filteredRequestedParts = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    return (requestedPartsData || []).filter((item) => {
      if (!normalizedSearch) {
        return true;
      }

      return [
        item?.partName,
        item?.supplierCompanyName,
        item?.staffName,
        String(item?.unitsUsed ?? ""),
      ]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(normalizedSearch));
    });
  }, [requestedPartsData, searchText]);

  const activeTableData =
    activeTab === PARTS_TAB ? filteredParts : filteredRequestedParts;

  const displayData =
    activeTableData?.slice(
      currentPage * usersPerPage,
      (currentPage + 1) * usersPerPage,
    ) || [];

  const handlePageClick = (data) => {
    setCurrentPage(data.selected);
  };

  if (
    (activeTab === PARTS_TAB && partsLoading && !partsData.length) ||
    (activeTab === REQUESTED_PARTS_TAB &&
      requestedPartsLoading &&
      !requestedPartsData.length)
  ) {
    return <Loader />;
  }

  return (
    <div className="ct_dashbaord_bg">
      <div className={`ct_dashbaord_main ${isToggle === false && "ct_active"}`}>
        <Sidebar path="part-usage" />
        <div className="ct_content_right">
          <Header onClick={onHandleClick} />
          <div className="ct_px_23 ct_mt_30 pb-5">

            <ul className="nav nav-tabs part-usage-tabs gap-3">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === PARTS_TAB ? "active" : ""}`}
                  type="button"
                  onClick={() => setActiveTab(PARTS_TAB)}
                >
                  Parts
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === REQUESTED_PARTS_TAB ? "active" : ""}`}
                  type="button"
                  onClick={() => setActiveTab(REQUESTED_PARTS_TAB)}
                >
                  Requested Parts
                </button>
              </li>
            </ul>

            <div className="d-flex mt-4  justify-content-end align-items-center ct_flex_wrap_767 gap-md-5 gap-4 ct_mb_32 w-100">
              <div className="search-box">
                <div className="input-group position-relative">
                  <input
                    className="form-control ct_input ct_input_ps_40 ct_transparent_input"
                    placeholder={
                      activeTab === PARTS_TAB
                        ? "Search part name, number or manufacturer"
                        : "Search part name or quantity"
                    }
                    type="text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  <i className="fas fa-search ct_show_search"></i>
                </div>
              </div>
              {activeTab === PARTS_TAB && (
                <div className="d-flex ct_w_100_767 ct_flex_col_575 gap-4">
                  <div className="dropdown ct_w_100_767 d-flex">
                    <button
                      className="btn filter-dropdown flex-grow-1 ct_w_100_575 d-flex align-items-center gap-4 justify-content-between text-nowrap"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      {selectedStatus}{" "}
                      <i className="fa-solid fa-angle-down"></i>
                    </button>
                    <ul className="dropdown-menu ct_custom_dropdown_filter">
                      {[
                        "All Status",
                        "In Stock",
                        "Low Stock",
                        "Out of Stock",
                      ].map((status) => (
                        <li className="ct_pointer" key={status}>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => setSelectedStatus(status)}
                          >
                            {status}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="ct_w_100_767">
                    <button
                      type="button"
                      data-bs-toggle="modal"
                      data-bs-target="#ct_add_parts_modal_post"
                      className="ct_orange_btn ct_w_100_575 flex-grow-1 border-0"
                    >
                      <svg
                        className="me-2"
                        width="22"
                        height="22"
                        viewBox="0 0 22 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11 20.625C5.6925 20.625 1.375 16.3075 1.375 11C1.375 5.6925 5.6925 1.375 11 1.375C16.3075 1.375 20.625 5.6925 20.625 11C20.625 16.3075 16.3075 20.625 11 20.625ZM11 2.75C6.44875 2.75 2.75 6.44875 2.75 11C2.75 15.5512 6.44875 19.25 11 19.25C15.5512 19.25 19.25 15.5512 19.25 11C19.25 6.44875 15.5512 2.75 11 2.75Z"
                          fill="white"
                        />
                        <path
                          d="M11 15.8125C10.615 15.8125 10.3125 15.51 10.3125 15.125V6.875C10.3125 6.49 10.615 6.1875 11 6.1875C11.385 6.1875 11.6875 6.49 11.6875 6.875V15.125C11.6875 15.51 11.385 15.8125 11 15.8125Z"
                          fill="white"
                        />
                        <path
                          d="M15.125 11.6875H6.875C6.49 11.6875 6.1875 11.385 6.1875 11C6.1875 10.615 6.49 10.3125 6.875 10.3125H15.125C15.51 10.3125 15.8125 10.615 15.8125 11C15.8125 11.385 15.51 11.6875 15.125 11.6875Z"
                          fill="white"
                        />
                      </svg>
                      Add Parts
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="ct_white_bg p-4 ct_border_radius_12 ct_mt_30">
              <div className="custom_table_wrapper">
                <div className="table-responsive ct_custom_table">
                  <table className="table">
                    <thead>
                      {activeTab === PARTS_TAB ? (
                        <tr>
                          <th>S.No</th>
                          <th>Part Name</th>
                          <th>Part Number</th>
                          <th>Manufacturer</th>
                          <th>Serial Number</th>
                          <th>Original Cost ({curSym})</th>
                          <th>Boat Owner Cost ({curSym})</th>
                          <th>Stock Quantity</th>
                          <th>Low Stock Alert</th>
                          {/* <th>Warranty</th> */}
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      ) : (
                        <tr>
                          <th>S.No</th>
                          <th>Part Name</th>
                          <th>Quantity</th>
                          <th>Requested By</th>
                          <th>Job Number</th>
                          <th>Action</th>
                        </tr>
                      )}
                    </thead>
                    <tbody>
                      {displayData.length > 0 ? (
                        displayData.map((item, index) => {
                          if (activeTab === REQUESTED_PARTS_TAB) {
                            return (
                              <tr key={item?.id}>
                                <td className="ct_text_para">
                                  {currentPage * usersPerPage + index + 1}
                                </td>
                                <td className="ct_text_para">
                                  {item?.partName || "-"}
                                </td>
                                <td className="ct_text_para">
                                  {item?.unitsUsed ?? "-"}
                                </td>
                                <td className="ct_text_para">
                                  {item?.requesterName || item?.supplierCompanyName || item?.staffName || "Supplier"}
                                </td>
                                <td className="ct_text_para">
                                  {item?.jobNumber || "-"}
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className=" border-0 p-0 ct_border_radius_10"
                                    style={{ backgroundColor: "var(--green)", width: "36px", height: "36px" }}
                                    data-bs-toggle="modal"
                                    data-bs-target="#ct_accept_requested_part_modal"
                                    onClick={() => setSelectedRequest(item)}
                                    title="Accept Request"
                                  >
                                    <svg
                                      width="18"
                                      height="18"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M20 7L10 17L5 12"
                                        stroke="#fff"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </button>
                                </td>
                              </tr>
                            );
                          }

                          const stockStatus = getPartStatus(item);
                          // const warrantyLabel = item?.warranty_duration
                          //   ? `${item.warranty_duration}${item?.warranty_type ? ` ${item.warranty_type}` : ""}`
                          //   : "-";

                          return (
                            <tr key={item?.id}>
                              <td className="ct_text_para">
                                {currentPage * usersPerPage + index + 1}
                              </td>
                              <td className="ct_text_para">{item?.name}</td>
                              <td className="ct_text_para">
                                {item?.part_number || "-"}
                              </td>
                              <td className="ct_text_para">
                                {item?.manufacturer || "-"}
                              </td>
                              <td className="ct_text_para">
                                {item?.serial_number || "-"}
                              </td>
                              <td className="ct_text_para">
                                ${item?.original_cost}
                              </td>
                              <td className="ct_text_para">
                                ${item?.boat_owner_cost}
                              </td>
                              <td className="ct_text_para">
                                {item?.stock_quantity ?? "-"}
                              </td>
                              <td className="ct_text_para">
                                {item?.low_stock_alert ?? "-"}
                              </td>
                              {/* <td className="ct_text_para">{warrantyLabel}</td> */}
                              <td className="ct_text_para">
                                <div className="d-flex align-items-center gap-2">
                                  {stockStatus === "In Stock" && (
                                    <>
                                      <span className="active-dot"></span>
                                      <span className="ct_active_badge">
                                        In Stock
                                      </span>
                                    </>
                                  )}

                                  {stockStatus === "Low Stock" && (
                                    <>
                                      <span className="pending-dot"></span>
                                      <span className="ct_pending_badge">
                                        Low Stock
                                      </span>
                                    </>
                                  )}

                                  {stockStatus === "Out of Stock" && (
                                    <>
                                      <span className="inactive-dot"></span>
                                      <span className="ct_inactive_badge">
                                        Out of Stock
                                      </span>
                                    </>
                                  )}
                                </div>
                              </td>
                              {/* <td className="ct_text_para">
                                {item?.warranty_document_url ? (
                                  <img
                                    src={getFileUrl(item?.warranty_document_url)}
                                    alt={`${item?.name || "Part"} warranty`}
                                    style={{
                                      width: "48px",
                                      height: "48px",
                                      objectFit: "cover",
                                      borderRadius: "8px",
                                    }}
                                  />
                                  ) : (
                                    "-"
                                  )}
                              </td> */}
                              <td>
                                <div className="d-flex gap-2">
                                  <button
                                    type="button"
                                    className="bg-transparent border-0 p-0"
                                    data-bs-toggle="modal"
                                    data-bs-target="#ct_edit_parts_usagemodal_post"
                                    onClick={() => handleEditClick(item)}
                                  >
                                    <svg
                                      width="18"
                                      height="18"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M13.7476 3.00008C14.1402 2.60753 14.6726 2.38696 15.2276 2.38696C15.7825 2.38696 16.3149 2.60753 16.7076 3.00008L20.9996 7.29208C21.3921 7.68472 21.6127 8.21712 21.6127 8.77208C21.6127 9.32704 21.3921 9.85944 20.9996 10.2521L10.4996 20.7521L6.24957 21.7521C5.91805 21.8301 5.57007 21.7316 5.3281 21.4896C5.08613 21.2476 4.98755 20.8996 5.06557 20.5681L6.06557 16.3181L16.5656 5.81808L13.7476 3.00008Z"
                                        stroke="#23C0E8"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </button>
                                  <button
                                    type="button"
                                    className="bg-transparent border-0 p-0"
                                    data-bs-toggle="modal"
                                    data-bs-target="#delete_part_modal"
                                    onClick={() => setSelectedPartId(item?.id)}
                                  >
                                    <svg
                                      width="15"
                                      height="17"
                                      viewBox="0 0 15 17"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M5.76786 2.71429H9.16071C9.16071 2.26436 8.98198 1.83287 8.66384 1.51473C8.3457 1.19659 7.91421 1.01786 7.46428 1.01786C7.01436 1.01786 6.58287 1.19659 6.26473 1.51473C5.94659 1.83287 5.76786 2.26436 5.76786 2.71429ZM4.75 2.71429C4.75 1.99441 5.03597 1.30402 5.54499 0.794996C6.05402 0.285969 6.74441 0 7.46428 0C8.18416 0 8.87455 0.285969 9.38357 0.794996C9.8926 1.30402 10.1786 1.99441 10.1786 2.71429H14.4196C14.5546 2.71429 14.6841 2.7679 14.7795 2.86335C14.875 2.95879 14.9286 3.08824 14.9286 3.22321C14.9286 3.35819 14.875 3.48764 14.7795 3.58308C14.6841 3.67852 14.5546 3.73214 14.4196 3.73214H13.5307L12.7049 13.6413C12.6448 14.3622 12.316 15.0341 11.7837 15.524C11.2514 16.0138 10.5545 16.2857 9.83114 16.2857H5.09743C4.37407 16.2857 3.67714 16.0138 3.14484 15.524C2.61255 15.0341 2.28376 14.3622 2.22368 13.6413L1.39786 3.73214H0.508929C0.373952 3.73214 0.244504 3.67852 0.149062 3.58308C0.053619 3.48764 0 3.35819 0 3.22321C0 3.08824 0.053619 2.95879 0.149062 2.86335C0.244504 2.7679 0.373952 2.71429 0.508929 2.71429H4.75ZM3.23814 13.5565C3.27695 14.023 3.48964 14.4578 3.83403 14.7748C4.17842 15.0918 4.62936 15.2678 5.09743 15.2679H9.83114C10.2992 15.2678 10.7502 15.0918 11.0945 14.7748C11.4389 14.4578 11.6516 14.023 11.6904 13.5565L12.5101 3.73214H2.41911L3.23814 13.5565ZM5.9375 6.10714C6.07248 6.10714 6.20192 6.16076 6.29737 6.2562C6.39281 6.35165 6.44643 6.48109 6.44643 6.61607V12.3839C6.44643 12.5189 6.39281 12.6484 6.29737 12.7438C6.20192 12.8392 6.07248 12.8929 5.9375 12.8929C5.80252 12.8929 5.67308 12.8392 5.57763 12.7438C5.48219 12.6484 5.42857 12.5189 5.42857 12.3839V6.61607C5.42857 6.48109 5.48219 6.35165 5.57763 6.2562C5.67308 6.16076 5.80252 6.10714 5.9375 6.10714ZM9.5 6.61607C9.5 6.48109 9.44638 6.35165 9.35094 6.2562C9.2555 6.16076 9.12605 6.10714 8.99107 6.10714C8.85609 6.10714 8.72665 6.16076 8.6312 6.2562C8.53576 6.35165 8.48214 6.48109 8.48214 6.61607V12.3839C8.48214 12.5189 8.53576 12.6484 8.6312 12.7438C8.72665 12.8392 8.85609 12.8929 8.99107 12.8929C9.12605 12.8929 9.2555 12.8392 9.35094 12.7438C9.44638 12.6484 9.5 12.5189 9.5 12.3839V6.61607Z"
                                        fill="#CB0B03"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan={activeTab === PARTS_TAB ? "12" : "4"}
                            className="text-center py-4"
                          >
                            {activeTab === PARTS_TAB
                              ? "No Parts Found"
                              : "No Requested Parts Found"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div
                  ref={addPartModalRef}
                  className="modal fade"
                  id="ct_add_parts_modal_post"
                  tabIndex={-1}
                  aria-labelledby="ct_add_parts_postLabel"
                  aria-hidden="true"
                >
                  <div className="modal-dialog modal-xl modal-dialog-centered">
                    <div className="modal-content ct_border_radius_20">
                      <div className="modal-body">
                        <div className="ct_delete_post_modal p-4">
                          <figure>
                            <figcaption>
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <h4 className="ct_fs_24 ct_fw_600 ct_blue_text mb-2">
                                    Add Part
                                  </h4>
                                  <p className="mb-0">
                                    Add parts to track usage, cost, warranty,
                                    and stock.
                                  </p>
                                </div>
                                <div
                                  data-bs-dismiss="modal"
                                  className="ct_pointer"
                                >
                                  <i className="fa-solid fa-xmark"></i>
                                </div>
                              </div>
                            </figcaption>
                            <Formik
                              initialValues={defaultPartValues}
                              validationSchema={AddPartSchema}
                              onSubmit={handleAddPart}
                            >
                              {({
                                values,
                                errors,
                                touched,
                                handleBlur,
                                handleChange,
                                handleSubmit,
                                isSubmitting,
                                setFieldValue,
                              }) => (
                                <>
                                  <form
                                    className="mt-5"
                                    onSubmit={handleSubmit}
                                  >
                                    <PartFormFields
                                      values={values}
                                      errors={errors}
                                      touched={touched}
                                      handleBlur={handleBlur}
                                      handleChange={handleChange}
                                      setFieldValue={setFieldValue}
                                      isEditMode={false}
                                    />
                                  </form>
                                  <div className="d-flex  justify-content-end border-0 gap-2 ct_modal_footer ct_mt_30">
                                    <button
                                      type="button"
                                      data-bs-dismiss="modal"
                                      className="ct_border_radius_5 ct_h_40 ct_orange_transparent_btn  text-center ct_pointer "
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      className="ct_border_radius_5 ct_h_40  ct_orange_btn px-5 text-center ct_pointer border-0"
                                      onClick={handleSubmit}
                                      disabled={isSubmitting}
                                    >
                                      {isSubmitting ? "Adding..." : "Add Part"}
                                    </button>
                                  </div>
                                </>
                              )}
                            </Formik>
                          </figure>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  ref={editPartModalRef}
                  className="modal fade"
                  id="ct_edit_parts_usagemodal_post"
                  tabIndex={-1}
                  aria-labelledby="ct_edit_parts_usagemodal_postLabel"
                  aria-hidden="true"
                >
                  <div className="modal-dialog modal-xl modal-dialog-centered">
                    <div className="modal-content ct_border_radius_20">
                      <div className="modal-body">
                        <div className="ct_delete_post_modal p-4">
                          <figure>
                            <figcaption>
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <h4 className="ct_fs_24 ct_fw_600 ct_blue_text mb-2">
                                    Edit Part
                                  </h4>
                                </div>
                                <div
                                  data-bs-dismiss="modal"
                                  className="ct_pointer"
                                >
                                  <i className="fa-solid fa-xmark"></i>
                                </div>
                              </div>
                            </figcaption>
                            <Formik
                              enableReinitialize
                              initialValues={editInitialValues}
                              validationSchema={AddPartSchema}
                              onSubmit={handleEditPart}
                            >
                              {({
                                values,
                                errors,
                                touched,
                                handleBlur,
                                handleChange,
                                handleSubmit,
                                isSubmitting,
                                setFieldValue,
                              }) => (
                                <>
                                  <form
                                    className="mt-5"
                                    onSubmit={handleSubmit}
                                  >
                                    <PartFormFields
                                      values={values}
                                      errors={errors}
                                      touched={touched}
                                      handleBlur={handleBlur}
                                      handleChange={handleChange}
                                      setFieldValue={setFieldValue}
                                      isEditMode
                                    // existingWarrantyDocument={
                                    //   resolvedSelectedPart?.warranty_document_url
                                    // }
                                    // existingPartImage={
                                    //   resolvedSelectedPart?.part_image_url
                                    // }
                                    />
                                  </form>
                                  <div className="d-flex c justify-content-end border-0 gap-2 ct_modal_footer ct_mt_30">
                                    <button
                                      type="button"
                                      data-bs-dismiss="modal"
                                      className="ct_border_radius_5 ct_h_40 ct_orange_transparent_btn  text-center ct_pointer "
                                      onClick={closeEditPartModal}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      className="ct_border_radius_5 ct_h_40 ct_orange_btn px-5 text-center ct_pointer border-0"
                                      onClick={handleSubmit}
                                      disabled={isSubmitting}
                                    >
                                      {isSubmitting
                                        ? "Saving..."
                                        : "Save Changes"}
                                    </button>
                                  </div>
                                </>
                              )}
                            </Formik>
                          </figure>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  ref={acceptRequestModalRef}
                  className="modal fade"
                  id="ct_accept_requested_part_modal"
                  tabIndex={-1}
                  aria-labelledby="ct_accept_requested_part_modalLabel"
                  aria-hidden="true"
                >
                  <div className="modal-dialog modal-xl modal-dialog-centered">
                    <div className="modal-content ct_border_radius_20">
                      <div className="modal-body">
                        <div className="ct_delete_post_modal p-4">
                          <figure>
                            <figcaption>
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <h4 className="ct_fs_24 ct_fw_600 ct_blue_text mb-2">
                                    Accept Request
                                  </h4>
                                  <p className="mb-0">
                                    Add the requested part into inventory and
                                    fulfill this request.
                                  </p>
                                </div>
                                <div
                                  data-bs-dismiss="modal"
                                  className="ct_pointer"
                                >
                                  <i className="fa-solid fa-xmark"></i>
                                </div>
                              </div>
                            </figcaption>
                            <Formik
                              enableReinitialize
                              initialValues={acceptRequestInitialValues}
                              validationSchema={AddPartSchema}
                              onSubmit={handleAcceptRequest}
                            >
                              {({
                                values,
                                errors,
                                touched,
                                handleBlur,
                                handleChange,
                                handleSubmit,
                                isSubmitting,
                                setFieldValue,
                              }) => (
                                <>
                                  <form
                                    className="mt-5"
                                    onSubmit={handleSubmit}
                                  >
                                    <PartFormFields
                                      values={values}
                                      errors={errors}
                                      touched={touched}
                                      handleBlur={handleBlur}
                                      handleChange={handleChange}
                                      setFieldValue={setFieldValue}
                                      isEditMode
                                    />
                                  </form>
                                  <div className="d-flex ct_flex_col_575 justify-content-end border-0 gap-2 ct_modal_footer ct_mt_30">
                                    <button
                                      type="button"
                                      data-bs-dismiss="modal"
                                      className="ct_border_radius_5 ct_h_40 ct_w_100_575 ct_orange_transparent_btn  text-center ct_pointer"
                                      onClick={closeAcceptRequestModal}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      className="ct_border_radius_5 ct_h_40 ct_w_100_575 ct_orange_btn px-5 text-center ct_pointer border-0"
                                      onClick={handleSubmit}
                                      disabled={isSubmitting}
                                    >
                                      {isSubmitting
                                        ? "Submitting..."
                                        : "Accept Request"}
                                    </button>
                                  </div>
                                </>
                              )}
                            </Formik>
                          </figure>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <DeleteModal
                  modalId="delete_part_modal"
                  handleDelete={handleDeletePart}
                  heading="Delete Part"
                  value="Are you sure you want to delete this part?"
                />
              </div>
            </div>

            <div className="mt-3">
              {activeTableData.length > 0 && (
                <div className="d-flex align-items-center flex-wrap justify-content-between gap-3 mb-3">
                  <PaginationDropdown
                    onChange={(val) => {
                      setUserPerPages(Number(val));
                      setCurrentPage(0);
                    }}
                  />
                  <ReactPagination
                    pageCount={Math.ceil(activeTableData.length / usersPerPage)}
                    onPageChange={handlePageClick}
                    currentPage={currentPage}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PartFormFields = ({
  values,
  errors,
  touched,
  handleBlur,
  handleChange,
  setFieldValue,
  isEditMode = false,
  // existingWarrantyDocument,
  // existingPartImage,
}) => {
  return (
    <div className="row">
      <div className="col-lg-6">
        <div className="form-group mb-3">
          <label className="form-label">Part Name</label>
          <input
            placeholder="Enter Part Name"
            type="text"
            className="form-control ct_input ct_transparent_input"
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            readOnly={isEditMode}
          />
          <ErrorMessage errors={errors} touched={touched} fieldName="name" />
        </div>
      </div>

      <div className="col-lg-6">
        <div className="form-group mb-3">
          <label className="form-label">Part Number</label>
          <input
            placeholder="Enter Part Number"
            type="text"
            className="form-control ct_input ct_transparent_input"
            name="part_number"
            value={values.part_number}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <ErrorMessage
            errors={errors}
            touched={touched}
            fieldName="part_number"
          />
        </div>
      </div>

      <div className="col-lg-6">
        <div className="form-group mb-3">
          <label className="form-label">Manufacturer</label>
          <input
            placeholder="Enter Manufacturer"
            type="text"
            className="form-control ct_input ct_transparent_input"
            name="manufacturer"
            value={values.manufacturer}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <ErrorMessage
            errors={errors}
            touched={touched}
            fieldName="manufacturer"
          />
        </div>
      </div>

      <div className="col-lg-6">
        <div className="form-group mb-3">
          <label className="form-label">Serial Number</label>
          <input
            placeholder="Enter Serial Number"
            type="text"
            className="form-control ct_input ct_transparent_input"
            name="serial_number"
            value={values.serial_number}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <ErrorMessage
            errors={errors}
            touched={touched}
            fieldName="serial_number"
          />
        </div>
      </div>

      <div className="col-lg-6">
        <div className="form-group mb-3">
          <label className="form-label">Original Cost ({curSym})</label>
          <input
            placeholder="Enter Original Cost"
            type="number"
            className="form-control ct_input ct_transparent_input"
            name="original_cost"
            value={values.original_cost}
            onChange={handleChange}
            onBlur={handleBlur}
            onWheel={(e) => e.target.blur()} />
          <ErrorMessage
            errors={errors}
            touched={touched}
            fieldName="original_cost"
          />
        </div>
      </div>

      <div className="col-lg-6">
        <div className="form-group mb-3">
          <label className="form-label">Boat Owner Cost ({curSym})</label>
          <input
            placeholder="Enter Boat Owner Cost"
            type="number"
            className="form-control ct_input ct_transparent_input"
            name="boat_owner_cost"
            value={values.boat_owner_cost}
            onChange={handleChange}
            onBlur={handleBlur}
            onWheel={(e) => e.target.blur()}
          />
          <ErrorMessage
            errors={errors}
            touched={touched}
            fieldName="boat_owner_cost"
          />
        </div>
      </div>

      <div className="col-lg-6">
        <div className="form-group mb-3">
          <label className="form-label">Stock Quantity</label>
          <input
            placeholder="Enter Stock Quantity"
            type="number"
            className="form-control ct_input ct_transparent_input"
            name="stock_quantity"
            value={values.stock_quantity}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <ErrorMessage
            errors={errors}
            touched={touched}
            fieldName="stock_quantity"
          />
        </div>
      </div>

      <div className="col-lg-6">
        <div className="form-group mb-3">
          <label className="form-label">Low Stock Alert</label>
          <input
            placeholder="Enter Low Stock Alert"
            type="number"
            className="form-control ct_input ct_transparent_input"
            name="low_stock_alert"
            value={values.low_stock_alert}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <ErrorMessage
            errors={errors}
            touched={touched}
            fieldName="low_stock_alert"
          />
        </div>
      </div>
      {/* 
      <div className="col-lg-6">
        <div className="form-group mb-3">
          <label className="form-label">Warranty Duration</label>
          <input
            placeholder="Enter Warranty Duration"
            type="number"
            className="form-control ct_input ct_transparent_input"
            name="warranty_duration"
            value={values.warranty_duration}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <ErrorMessage
            errors={errors}
            touched={touched}
            fieldName="warranty_duration"
          />
        </div>
      </div>

      <div className="col-lg-6">
        <div className="form-group mb-3">
          <label className="form-label">Warranty Type</label>
          <select
            className="form-control ct_input ct_transparent_input"
            name="warranty_type"
            value={values.warranty_type}
            onChange={handleChange}
            onBlur={handleBlur}
          >
            {WARRANTY_TYPE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <ErrorMessage
            errors={errors}
            touched={touched}
            fieldName="warranty_type"
          />
        </div>
      </div> */}



      {/* <div className="col-lg-6">
        <div className="form-group mb-3">
          <label className="form-label">Warranty Document</label>
          <input
            type="file"
            className="form-control ct_input ct_transparent_input"
            accept="image/*,.pdf"
            onChange={(event) =>
              setFieldValue("warranty_document", event.currentTarget.files?.[0] || null)
            }
          />
          {existingWarrantyDocument && !values.warranty_document ? (
            <img
              src={getFileUrl(existingWarrantyDocument)}
              alt="Current warranty document"
              className="ct_uploaded_img mt-2"
              style={{ width: "72px", height: "72px", objectFit: "cover" }}
            />
          ) : null}
        </div>
      </div> */}

      {/* <div className="col-lg-6">
        <div className="form-group mb-3">
          <label className="form-label">Part Image</label>
          <input
            type="file"
            className="form-control ct_input ct_transparent_input"
            accept="image/*"
            onChange={(event) =>
              setFieldValue("part_image", event.currentTarget.files?.[0] || null)
            }
          />
          {existingPartImage && !values.part_image ? (
            <img
              src={getFileUrl(existingPartImage)}
              alt="Current part"
              className="ct_uploaded_img mt-2"
              style={{ width: "72px", height: "72px", objectFit: "cover" }}
            />
          ) : null}
        </div>
      </div> */}
    </div>
  );
};

export default PartUsage;
